import VideoAnalytics from "@framework/analytics/components/VideoAnalytics";
import { CHORUS_DO_NOT_TRACK_COOKIE } from "@framework/config/app.config";
import { getCookie } from "@voxmedia/duet-utils/cookie";
import iframeClient, {
  XFrameTypes,
} from "@voxmedia/duet-utils/video/iframe-client";
import Viewport from "@voxmedia/duet-utils/video/viewport";

declare global {
  interface Window {
    volume_debug?: string;
  }
}

const Analytics = new VideoAnalytics();
const IframeClient = new iframeClient();

let xframe: XFrameTypes | null;

/**
 * Volume external API.
 * Install this script on the HOST PAGE that will include embeds.
 *
 * File taken and modified from SBN:
 * https://github.com/voxmedia/sbn/blob/master/app_unison/frontend/scripts/lib/video.js
 * Beware if you try to refactor this file, theres logic/calls tied to Unison no-one understands
 *
 * Features:
 * <ul>
 *  <li>Installs iframe embeds into Volume placeholder elements.</li>
 *  <li>Sends "view" messages to embeds when scrolled into viewport.</li>
 *  <li>Provides proxy API for sending messages to iframe embeds.</li>
 * </ul>
 * @param {String|Element|Window} target selector string, iframe element, or target window.
 * @example
 * import volume from 'lib/video';
 * volume.install('http://staging.volume.voxmedia.com');
 * volume('#my-embed').play();
 * volume('#my-embed').pause();
 *
 * @module lib/video
 */
function volume(target: string | Window | Element) {
  return new VolumeClient(target);
}

/**
 * Gets the DOM placement value for an element.
 * This traverses up the DOM tree looking for placement attributions.
 * @private
 */
function getAnalyticsPlacement(el: Element | null) {
  let placement;
  while (!placement && el && el.hasAttributes()) {
    placement = el.getAttribute("data-analytics-placement");
    if (placement) {
      return placement;
    } else {
      el = el.parentElement;
    }
  }
  return "other";
}

/**
 * This must be set while calling `.install()`.
 */
volume.embedHost = "";

/**
 * Selects all Volume iframe embeds on the page.
 */
volume.selectAllEmbeds = function () {
  return document.querySelectorAll(
    'iframe[src^="' + this.embedHost + '/embed"]',
  );
};

/**
 * Installs all Volume video placeholders into the document,
 * and configures viewport tracking for new and existing embeds.
 * Call this after adding any new Volume embeds/placeholders into the document.
 * (invoking this method multiple times per page is OKAY)
 * NOTE: this is imported and called within the "foundation/base" component.
 * @param {String} [host] optional URL to use as the embed host.
 * @example
 * volume.install();
 * volume.install('http://staging.volume.voxmedia.com');
 */
volume.install = function (host: string) {
  // TODO: (cvle) figure out if we can watch for a different cookie (gtm) to do this?
  if (getCookie(CHORUS_DO_NOT_TRACK_COOKIE)) {
    return;
  }

  this.embedHost = host;

  this.log("install " + this.embedHost);
  const embeds = this.selectAllEmbeds();
  let i;

  if (!xframe) {
    // Install cross-frame message bus during initial request:
    // this will run once during the first installation of Volume on the page.
    // This message bus handles all communication with Volume iframe instances.
    xframe = IframeClient.create("volume", this.embedHost)
      .on(
        "analytics",
        (_e: any, data: any) => {
          volume.reportEvent(data);
        },
        this,
      )
      .on(
        "pauseOthers",
        (e: { source: Window }) => {
          volume.postToAll("pause", undefined, e.source);
        },
        this,
      )
      .on(
        "host",
        () => {
          return {
            url: location.href,
            origin: location.origin,
            referrer: document.referrer,
            title: document.title,
            api: true,
          };
        },
        this,
      )
      .listen();
  } else {
    // If a cross-frame messaging bus already exists,
    // then just update its host protocol with the latest Volume host setting.
    // This host setting specifies what domain the messenger is allowed to talk to.
    xframe.host = this.embedHost;
  }

  // Register all existing iframe embeds with viewport tracking:
  for (i = 0; i < embeds.length; i += 1) {
    this.trackView(embeds[i] as HTMLIFrameElement);
  }

  return this;
};

/**
 * Uninstalls the Volume client API.
 * Cancels listeners and stops polling requests.
 */
volume.uninstall = function () {
  if (xframe) {
    xframe.dispose();
    xframe = null;
  }
};

/**
 * Report event data to analytics tracking.
 * @param {Object} eventData data object to report.
 */
volume.reportEvent = function (eventData: any) {
  Analytics.event(eventData);
  this.log(eventData);
};

/**
 * Logs debugging information into the console (when enabled).
 * @param {String} message to pass through to the console.
 */
volume.log = function (message: any) {
  if (window.volume_debug) {
    console.log("volume: ", message);
  }
};

/**
 * Viewport tracking for a single iframe embed element.
 * Sends a "view" message/event when the target element enters the viewport.
 * Elements must be a Volume iframe or placeholder div.
 * All view requests are indexed by SKU (stock-keeping unit),
 * so that unique iframe/placeholder pairings are only tracked once.
 * @param {Element} el (volume iframe or placeholder div) to track within viewport.
 * @param {String} placement code indicating the element's position on the page.
 */
volume.trackView = function (el: HTMLIFrameElement, placement?: string) {
  placement = placement || getAnalyticsPlacement(el);

  // Acquire Volume iframes from provided element:
  // The iframe could be the element itself, or the wrapper.
  const uuidForIframe = /embed\/([^/?]+)/;
  const getEmbedIframe = () => {
    const iframeEl = /iframe/i.test(el.tagName)
      ? el
      : el.querySelector("iframe");
    return iframeEl && uuidForIframe.test(iframeEl.src) ? iframeEl : null;
  };

  let embedEl = getEmbedIframe();
  const uuid = el.getAttribute("data-volume-uuid");

  // Manage tracking for unique stock keeping units:
  // This makes sure the same UUID/placement combo is only tracked once.
  // Pileups can occur if we have a placeholder and its embed both submitted for tracking.
  if (uuid) {
    const sku = `${uuid}-${placement}`;
    this.log("[tracking] " + sku);

    // Submit element for viewport tracking:
    Viewport.trackElement(
      el,
      function () {
        // Re-attempt to acquire iframe element once we're ready to report:
        // This assumes that a placeholder may not yet have had an iframe in it before.
        embedEl = embedEl || getEmbedIframe();
        if (embedEl) {
          // Report view via the volume iframe, if available.
          // Basically, we send a message to the iframe
          // telling it to tell us that it was viewed.
          // Crazy, but the advantage here is that the video
          // can report using its full Volume meta data.
          volume.log("[view] " + sku);
          const iframeEl = embedEl;
          setTimeout(
            () => volume(iframeEl).request("view", document.title),
            100,
          );
        } else {
          // Otherwise, report a generic event...
          // do we still want to do this?
          volume.log("[view fallback] " + sku);
          volume.reportEvent({
            eventCategory: "video",
            eventAction: ["volume", "view", placement].join(":"),
            eventLabel: [
              document.title,
              el.getAttribute("data-volume-id") || uuid,
            ].join(" | "),
            nonInteraction: true,
          });
        }
      },
      0.1,
    );
  }
};

/**
 * Posts a message to all Volume iframes.
 * Optionally allows for one origin source to be excluded.
 * @param {String|Object} message to send all Volume iframes.
 * @param {String} [value] to send along with the message.
 * @param {Window} excludeSrc window source; useful for proxying out messages from a sender.
 */
volume.postToAll = function (
  message: string | Record<string, any>,
  value?: string,
  excludeSrc?: Window,
) {
  const embeds = this.selectAllEmbeds();

  for (let i = 0; i < embeds.length; i++) {
    if (xframe) {
      const src = xframe.src(embeds[i] || null);
      if (src !== excludeSrc) {
        xframe.post(src, message, value);
      }
    }
  }
};

/**
 * Sends a pause message to all Volume iframes.
 */
volume.stopAll = function () {
  this.postToAll("pause");
};

/**
 * Volume Client Object
 * Creates a wrapper around a embedded content frame,
 * and handles all cross-frame communication with that embed.
 * @param {String|Element|Window} target selector or element of the iframe/window to harness.
 * @constructor
 */
class VolumeClient {
  el: Window | null;
  constructor(target: string | Element | Window) {
    if (!xframe) {
      throw new Error("volume is not installed");
    }
    this.el = xframe.src(target);
  }

  /**
   * Sends a "play" message to the video client.
   * @param {Object} opts; include "request: true" for message confirmation.
   * volume('#my-embed').play();
   * volume('#my-embed').play({ request: true });
   */
  play(opts: { request: any }) {
    const PLAY = "play";
    return opts.request ? this.request(PLAY) : this.post(PLAY);
  }

  /**
   * Sends a "pause" message to the video client.
   * @param {Object} opts; include "request: true" for message confirmation.
   * @example
   * volume('#my-embed').pause();
   * volume('#my-embed').pause({ request: true });
   */
  pause(opts: { request: any }) {
    const PAUSE = "pause";
    return opts.request ? this.request(PAUSE) : this.post(PAUSE);
  }

  /**
   * Posts a message and value to the video client.
   * This is a blind post without guarentee that the message will go through
   * (messages may fail if the client frame is not yet fully initialized).
   * Use `request` to post a message with confirmation of receipt.
   * @param {String|Object} message string or formatted object to send.
   * Passing an object requires that a "message" key be provided.
   * @param {Any} [value] an optional value to send with the message.
   * @returns {VolumeClient} self reference.
   * @example
   * volume('#my-embed').post('hello');
   * volume('#my-embed').post('hello', 'world');
   * volume('#my-embed').post({ message: 'hello', data: true });
   */
  post(message: string | Record<string, any>, value?: any): VolumeClient {
    if (xframe) {
      xframe.post(this.el, message, value);
    }
    return this;
  }

  /**
   * Posts a message to a video client with request for confirmation.
   * This implementation polls the client frame with the posted message
   * until the frame sends back a confirmation response.
   * Use this method to (better) guarentee delivery.
   * @param {String} message string to send.
   * @param {Any} [value] an optional value to send with the message.
   * @param {Function} callback function to handle response.
   * @returns {VolumeClient} self reference.
   */
  request(message: string, value?: any, callback?: () => void): VolumeClient {
    if (xframe) {
      xframe.request(this.el, message, value, callback);
    }
    return this;
  }
}

export default volume;
